/**
 * Sleeping Cots Tab Component
 * Manages sleeping cot assignments for naptime
 * Auto-generates Full Time children with option to add other categories
 * Persists cot assignments to database
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Moon, Printer, Trash2, Plus, Download, RefreshCw, Users, Save, Check } from "lucide-react";
import { format } from "date-fns";
import { exportSleepingCotsPDF } from "@/utils/pdfExport";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const SleepingCotsTab = ({ children, centerName = "Childcare Center" }) => {
  const [cotStaffName, setCotStaffName] = useState("");
  const [cotAssignments, setCotAssignments] = useState([]);
  const [cotNotes, setCotNotes] = useState("");
  const [additionalCategory, setAdditionalCategory] = useState("");
  const [showAddChildDropdown, setShowAddChildDropdown] = useState(false);
  const [savedAssignments, setSavedAssignments] = useState({}); // childId -> cotNumber mapping
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get children by category
  const fullTimeChildren = children.filter(c => 
    c.active !== false && 
    ['full_time', 'regular'].includes(c.category?.toLowerCase().replace(/\s+/g, '_'))
  );

  const partTimeChildren = children.filter(c => 
    c.active !== false && 
    c.category?.toLowerCase().replace(/\s+/g, '_') === 'part_time'
  );

  const schoolAgeChildren = children.filter(c => 
    c.active !== false && 
    c.category?.toLowerCase().replace(/\s+/g, '_') === 'school_age'
  );

  const afterSchoolChildren = children.filter(c => 
    c.active !== false && 
    c.category?.toLowerCase().replace(/\s+/g, '_') === 'after_school'
  );

  // All enrolled children for "Add Child" dropdown
  const allEnrolledChildren = children.filter(c => c.active !== false);

  // Load saved cot assignments on mount
  useEffect(() => {
    loadSavedAssignments();
  }, []);

  // Initialize assignments after loading saved data and children
  useEffect(() => {
    if (!isLoading && children.length > 0 && cotAssignments.length === 0) {
      initializeAssignments();
    }
  }, [isLoading, children]);

  const loadSavedAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cot-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create a mapping of childId -> saved assignment
      const saved = {};
      (response.data.assignments || []).forEach(a => {
        saved[a.childId] = a;
      });
      setSavedAssignments(saved);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading cot assignments:", error);
      setIsLoading(false);
    }
  };

  const initializeAssignments = () => {
    // Initialize with Full Time children, applying saved cot numbers
    const initialAssignments = fullTimeChildren.map((child) => ({
      id: `ft-${child.id}`,
      childId: child.id,
      childName: child.name,
      cotNumber: savedAssignments[child.id]?.cotNumber || "",
      category: "full_time",
      isAutoGenerated: true
    }));
    setCotAssignments(initialAssignments);
  };

  // Handle adding children from additional category
  const handleAddCategory = (category) => {
    setAdditionalCategory(category);
    
    let categoryChildren = [];
    switch(category) {
      case 'part_time':
        categoryChildren = partTimeChildren;
        break;
      case 'school_age':
        categoryChildren = schoolAgeChildren;
        break;
      case 'after_school':
        categoryChildren = afterSchoolChildren;
        break;
      default:
        return;
    }

    // Add children from selected category (avoid duplicates)
    const existingChildIds = new Set(cotAssignments.map(a => a.childId));
    const newAssignments = categoryChildren
      .filter(child => !existingChildIds.has(child.id))
      .map(child => ({
        id: `${category}-${child.id}`,
        childId: child.id,
        childName: child.name,
        cotNumber: savedAssignments[child.id]?.cotNumber || "", // Apply saved cot number if exists
        category: category,
        isAutoGenerated: true
      }));

    if (newAssignments.length > 0) {
      setCotAssignments([...cotAssignments, ...newAssignments]);
    }
  };

  // Remove a category's children (except Full Time which is constant)
  const handleRemoveCategory = (category) => {
    if (category === 'full_time') return; // Full Time is constant
    setCotAssignments(cotAssignments.filter(a => a.category !== category));
    if (additionalCategory === category) {
      setAdditionalCategory("");
    }
  };

  // Add individual child from any category
  const handleAddIndividualChild = (childId) => {
    const child = allEnrolledChildren.find(c => c.id === childId);
    if (!child) return;

    // Check if already added
    if (cotAssignments.some(a => a.childId === childId)) {
      return; // Already in list
    }

    const newAssignment = {
      id: `manual-${Date.now()}`,
      childId: child.id,
      childName: child.name,
      cotNumber: savedAssignments[child.id]?.cotNumber || "", // Apply saved cot number if exists
      category: child.category?.toLowerCase().replace(/\s+/g, '_') || 'other',
      isAutoGenerated: false
    };

    setCotAssignments([...cotAssignments, newAssignment]);
    setShowAddChildDropdown(false);
  };

  const handleUpdateCotNumber = (id, cotNumber) => {
    setCotAssignments(cotAssignments.map(a => 
      a.id === id ? { ...a, cotNumber } : a
    ));
    setHasUnsavedChanges(true);
  };

  const handleRemoveAssignment = async (id, childId) => {
    setCotAssignments(cotAssignments.filter(a => a.id !== id));
    
    // Also remove from saved assignments if it had a cot number
    if (savedAssignments[childId]) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/cot-assignments/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const newSaved = { ...savedAssignments };
        delete newSaved[childId];
        setSavedAssignments(newSaved);
        toast.success("Cot assignment removed");
      } catch (error) {
        console.error("Error removing cot assignment:", error);
      }
    }
  };

  // Save cot assignments to database
  const handleSaveAssignments = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const assignmentsToSave = cotAssignments.map(a => ({
        childId: a.childId,
        childName: a.childName,
        cotNumber: a.cotNumber,
        category: a.category
      }));

      await axios.put(`${API}/cot-assignments`, 
        { assignments: assignmentsToSave },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Update local saved state
      const newSaved = {};
      assignmentsToSave.forEach(a => {
        if (a.cotNumber) {
          newSaved[a.childId] = a;
        }
      });
      setSavedAssignments(newSaved);
      setHasUnsavedChanges(false);
      toast.success("Cot assignments saved!");
    } catch (error) {
      console.error("Error saving cot assignments:", error);
      toast.error("Failed to save cot assignments");
    } finally {
      setIsSaving(false);
    }
  };

  // Refresh to reset to Full Time only
  const handleRefreshList = () => {
    const initialAssignments = fullTimeChildren.map((child) => ({
      id: `ft-${child.id}`,
      childId: child.id,
      childName: child.name,
      cotNumber: savedAssignments[child.id]?.cotNumber || "", // Keep saved cot numbers
      category: "full_time",
      isAutoGenerated: true
    }));
    setCotAssignments(initialAssignments);
    setAdditionalCategory("");
  };

  const handlePrint = () => {
    window.print();
  };

  // Get category label
  const getCategoryLabel = (category) => {
    switch(category) {
      case 'full_time': return 'FT';
      case 'part_time': return 'PT';
      case 'school_age': return 'SA';
      case 'after_school': return 'AS';
      default: return '';
    }
  };

  // Get children not yet in the list for "Add Child" dropdown
  const availableChildren = allEnrolledChildren.filter(
    child => !cotAssignments.some(a => a.childId === child.id)
  );

  // Count how many have cot numbers assigned
  const assignedCount = cotAssignments.filter(a => a.cotNumber && a.cotNumber.trim()).length;

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-indigo-200">
      <CardHeader className="print:hidden">
        <CardTitle className="text-2xl text-indigo-800 flex items-center gap-3">
          <Moon className="w-7 h-7" />
          Sleeping Cots Report
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Image */}
        <div className="mb-6 print:hidden">
          <img 
            src="https://images.unsplash.com/photo-1649889385821-19ad2b3b37d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxzbGVlcGluZyUyMHRvZGRsZXIlMjBuYXB8ZW58MHx8fHwxNzY5ODk1MzU0fDA&ixlib=rb-4.1.0&q=85"
            alt="Sleeping child"
            className="w-full h-48 object-cover rounded-lg shadow-md"
          />
        </div>

        {/* Staff Name Input */}
        <div className="mb-6 print:hidden">
          <Label className="text-indigo-800 font-semibold">Staff Name</Label>
          <Input
            value={cotStaffName}
            onChange={(e) => setCotStaffName(e.target.value)}
            placeholder="Enter staff name"
            className="border-indigo-200 mt-1 max-w-md"
          />
        </div>

        {/* Category Controls */}
        <div className="mb-4 p-4 bg-indigo-50 rounded-lg print:hidden">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              <span className="font-semibold text-indigo-800">Categories:</span>
            </div>
            
            {/* Full Time badge (always shown, can't remove) */}
            <span className="px-3 py-1 bg-indigo-600 text-white rounded-full text-sm font-medium">
              Full Time ({fullTimeChildren.length})
            </span>

            {/* Additional category selector */}
            <select
              value=""
              onChange={(e) => e.target.value && handleAddCategory(e.target.value)}
              className="px-3 py-1 border border-indigo-300 rounded-lg text-sm bg-white"
            >
              <option value="">+ Add Category</option>
              {partTimeChildren.length > 0 && (
                <option value="part_time">Part Time ({partTimeChildren.length})</option>
              )}
              {schoolAgeChildren.length > 0 && (
                <option value="school_age">School Age ({schoolAgeChildren.length})</option>
              )}
              {afterSchoolChildren.length > 0 && (
                <option value="after_school">After School ({afterSchoolChildren.length})</option>
              )}
            </select>

            {/* Show added category badges with remove option */}
            {cotAssignments.some(a => a.category === 'part_time') && (
              <span className="px-3 py-1 bg-purple-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                Part Time
                <button onClick={() => handleRemoveCategory('part_time')} className="ml-1 hover:bg-purple-600 rounded-full p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            )}
            {cotAssignments.some(a => a.category === 'school_age') && (
              <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                School Age
                <button onClick={() => handleRemoveCategory('school_age')} className="ml-1 hover:bg-green-600 rounded-full p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            )}
            {cotAssignments.some(a => a.category === 'after_school') && (
              <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                After School
                <button onClick={() => handleRemoveCategory('after_school')} className="ml-1 hover:bg-orange-600 rounded-full p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            )}

            {/* Refresh button */}
            <Button
              onClick={handleRefreshList}
              variant="outline"
              size="sm"
              className="border-indigo-300 text-indigo-600 hover:bg-indigo-100"
              title="Reset to Full Time only"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset
            </Button>

            {/* Save button */}
            <Button
              onClick={handleSaveAssignments}
              size="sm"
              className={`${hasUnsavedChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'} text-white`}
              disabled={isSaving || !hasUnsavedChanges}
            >
              {isSaving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Cots
                </>
              )}
            </Button>
          </div>
          
          {/* Saved indicator */}
          {assignedCount > 0 && (
            <p className="text-sm text-indigo-600 mt-2 flex items-center gap-1">
              <Check className="w-4 h-4" />
              {assignedCount} cot{assignedCount !== 1 ? 's' : ''} assigned
              {hasUnsavedChanges && <span className="text-orange-500 ml-2">(unsaved changes)</span>}
            </p>
          )}
        </div>

        {/* Report Preview */}
        <div className="border-2 border-indigo-100 rounded-lg p-6 bg-white print:border-0 print:p-0">
          <div className="text-center mb-6 border-b-2 border-indigo-200 pb-4 print:border-black">
            <h2 className="text-2xl font-bold text-indigo-800 print:text-black">Sleeping Cots Assignment</h2>
            <div className="grid grid-cols-2 gap-4 mt-4 text-left max-w-md mx-auto">
              <div>
                <span className="font-semibold">Staff:</span>
                <p className="text-indigo-700 print:text-black">{cotStaffName || "Not Entered"}</p>
              </div>
              <div>
                <span className="font-semibold">Date:</span>
                <p className="text-indigo-700 print:text-black">{format(new Date(), 'MMMM d, yyyy')}</p>
              </div>
            </div>
          </div>

          {/* Cot Assignments Table */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-indigo-800 print:text-black">
                Cot Assignments ({cotAssignments.length})
              </h3>
              
              {/* Add Individual Child Dropdown */}
              <div className="relative print:hidden">
                <Button
                  onClick={() => setShowAddChildDropdown(!showAddChildDropdown)}
                  size="sm"
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                  disabled={availableChildren.length === 0}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Child
                </Button>
                
                {showAddChildDropdown && availableChildren.length > 0 && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-indigo-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {availableChildren.map(child => (
                      <button
                        key={child.id}
                        onClick={() => handleAddIndividualChild(child.id)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex justify-between items-center"
                      >
                        <span>{child.name}</span>
                        <span className="text-xs text-gray-500 capitalize">
                          {child.category?.replace(/_/g, ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <p className="text-gray-500 italic">Loading cot assignments...</p>
            ) : cotAssignments.length === 0 ? (
              <p className="text-gray-500 italic">No children to display. Full Time children will auto-populate.</p>
            ) : (
              <div className="space-y-2">
                {cotAssignments.map((assignment, index) => (
                  <div 
                    key={assignment.id} 
                    className="flex items-center gap-3 bg-indigo-50 p-3 rounded print:bg-white print:border-b print:p-2"
                  >
                    <span className="font-semibold text-indigo-600 print:text-black w-8">{index + 1}.</span>
                    <span className="flex-1 font-medium text-gray-800">{assignment.childName}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600 print:hidden">
                      {getCategoryLabel(assignment.category)}
                    </span>
                    <Input
                      value={assignment.cotNumber}
                      onChange={(e) => handleUpdateCotNumber(assignment.id, e.target.value)}
                      placeholder="Cot #"
                      className={`w-20 border-indigo-200 text-center ${assignment.cotNumber ? 'bg-green-50 border-green-300' : ''}`}
                    />
                    <Button
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.childId)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50 print:hidden"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-indigo-800 font-semibold print:text-black">Notes</Label>
            <Textarea
              value={cotNotes}
              onChange={(e) => setCotNotes(e.target.value)}
              placeholder="Enter any notes about sleeping arrangements..."
              className="border-indigo-200 mt-2 min-h-[100px] print:border-black"
            />
          </div>
        </div>

        {/* Print Button */}
        <div className="mt-6 flex justify-end gap-2 print:hidden">
          <Button 
            onClick={() => exportSleepingCotsPDF({
              date: format(new Date(), 'yyyy-MM-dd'),
              staffName: cotStaffName,
              cots: cotAssignments.map((cot, index) => ({
                cotNumber: cot.cotNumber || String(index + 1),
                childName: cot.childName,
                napTime: '',
                instructions: ''
              })),
              notes: cotNotes
            }, centerName)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SleepingCotsTab;
